import { useEffect, useId, useRef, type ReactNode } from "react";
import { Icon } from "./Icon.js";

/**
 * A panel that slides up over the screen, for something opened from one
 * row of a long list: who sees a task, what a person sees, a device's
 * enrolment code.
 *
 * Why an overlay and not a panel in the row. Both of the earlier
 * placements failed the same way — the panel appeared somewhere the eye
 * was not, first at the end of the list and then, fixed, inside a row
 * that could itself be scrolled half off the screen — and a button whose
 * result is out of view reads as a dead button. Pinned to the bottom of
 * the viewport, this cannot be out of view.
 *
 * Tapping outside closes it, and so does Escape, because a sheet that can
 * only be closed by finding its own Close button is a sheet somebody
 * signs out to escape.
 */
export function Sheet({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  // Held in a ref so a parent re-rendering with a new closure does not
  // re-run the effect and steal focus back to the panel mid-edit.
  const close = useRef(onClose);
  close.current = onClose;

  // Read during the first render, before any child's autoFocus has run.
  // Read in the effect, as it was until 2026-09-26, it was already the
  // autoFocused field — so closing "returned" focus to an input that was
  // about to unmount instead of to the button that opened the sheet.
  const opener = useRef<HTMLElement | null>(null);
  if (opener.current === null) opener.current = document.activeElement as HTMLElement | null;

  useEffect(() => {
    const before = opener.current;
    // Only when nothing inside has asked for focus: taking it from an
    // autoFocused field left "Add a type" opening with its Name field
    // unfocused.
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Back to the button that opened it, so a keyboard or switch user
      // is not dropped at the top of a long list.
      before?.focus?.();
    };
  }, []);

  return (
    <div className="sheet-backdrop">
      <button type="button" className="sheet-scrim" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div
        ref={panel}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-head">
          <div>
            <h2 id={id}>{title}</h2>
            {subtitle ? <p className="muted">{subtitle}</p> : null}
          </div>
          <button type="button" className="sheet-close" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={26} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
